import React, { useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';

import { useTranslation, useBoolean, useAuthorization } from 'hooks';
import { GET_DATA_REVIEW_TABLES } from 'queries/central-monitoring';
import { StudyFields, DataReviewTableFields } from 'queries/generated/fragments.generated';
import { GET_RESTRICTED_STUDY } from 'queries/studies';
import NotificationService from 'utils/notification';
import AddTable from 'views/components/admin/studies/data-review-tables/add-table';
import StudyReviewGrid from 'views/components/admin/studies/data-review-tables/data-review-grid';
import EditColumns from 'views/components/admin/studies/data-review-tables/edit-columns';
import Button from 'views/components/core/buttons/button';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';
import Constants from '@utils/constants';
import { dataReviewTables, dataReviewTablesVariables } from '@queries/generated/central-monitoring.generated';
import { getRestrictedStudy, getRestrictedStudyVariables } from '@queries/generated/studies.generated';

export interface DataReviewTableFieldsExtended extends DataReviewTableFields {
    _columnsNum: number;
    _baseData: string;
}

const AdminStudyDataReview = ({ study }: { study: StudyFields }) => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const canEdit = !!user?.canWriteSystemAdminLevel?.();
    const isSysAdmin = !!user?.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin);

    const [items, setItems] = useState<DataReviewTableFieldsExtended[]>([]);
    const [currentTable, setCurrentTable] = useState<DataReviewTableFieldsExtended | null>(null);

    const addTableOpen = useBoolean(false);
    const editColumnsOpen = useBoolean(false);

    const { loading, error, data } = useQuery<dataReviewTables, dataReviewTablesVariables>(GET_DATA_REVIEW_TABLES, {
        variables: { studyId: study?.id },
    });
    const [loadStudyData, { loading: loadingStudy, data: studyData }] = useLazyQuery<
        getRestrictedStudy,
        getRestrictedStudyVariables
    >(GET_RESTRICTED_STUDY);

    useEffect(() => {
        if (study) loadStudyData({ variables: { id: study.id } });
    }, [study]);

    useEffect(() => {
        if (data?.dataReviewTablesForStudy) {
            const updatedTables = data.dataReviewTablesForStudy.map(
                (item) =>
                    ({
                        ...item,
                        description: item?.description || '',
                        _columnsNum: item?.columns?.length,
                        _baseData: t(`admin:${item?.type}-REVIEW_TYPE`),
                    }) as DataReviewTableFieldsExtended,
            );
            setItems(updatedTables);
            if (currentTable) {
                const updatedTable = updatedTables.find((item) => item?.id === currentTable.id);
                setCurrentTable(updatedTable || null);
            }
        }
    }, [data]);

    const actions = (item: DataReviewTableFieldsExtended) => [
        {
            label: 'admin:editTable',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentTable(item);
                addTableOpen.setTrue();
            },
            enabled: canEdit || isSysAdmin,
        },
        {
            label: 'admin:editColumns',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentTable(item);
                editColumnsOpen.setTrue();
            },
            enabled: true,
        },
    ];

    if (!study) return <h3>{t('common:noStudySelected')}</h3>;

    if (!study || loading || loadingStudy) return <Loading />;

    if (error) return <Error error={error} />;

    return (
        <>
            {!!currentTable && editColumnsOpen.value && (
                <EditColumns
                    currentTable={currentTable}
                    onClose={() => {
                        setCurrentTable(null);
                        editColumnsOpen.setFalse();
                    }}
                    study={studyData?.study}
                    canEdit={canEdit}
                />
            )}
            <AddTable
                open={addTableOpen.value}
                study={study}
                tables={items}
                currentTable={currentTable}
                isSysAdmin={isSysAdmin}
                onClose={(action?: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'edit')
                            NotificationService.showNotification(
                                t('admin:tableSuccessfulUpdated', { id: currentTable?.recordId ?? currentTable?.id }),
                            );
                        else if (action === 'add')
                            NotificationService.showNotification(t('admin:tableSuccessfulCreated'));
                        setCurrentTable(null);
                        addTableOpen.setFalse();
                    }
                }}
            />
            <PageTitle text={t('admin:manageDataTables')} />
            <br />
            <Button
                id="add-table"
                className="add-cv-btn main-button"
                onClick={() => addTableOpen.setTrue()}
                disabled={!canEdit}
                style={{ display: 'flex', marginLeft: 'auto', marginBottom: 30 }}
            >
                {t('admin:addTable')}
            </Button>
            <StudyReviewGrid items={items} actions={actions} />
        </>
    );
};

export default AdminStudyDataReview;
